import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocalizedCurrency } from '../localization/LocalizedComponents';

interface Tier {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  benefits: string[];
}

interface GiftPurchaseData {
  tierId: string;
  months: number;
  recipientEmail?: string;
  recipientName?: string;
  message?: string;
  deliverAt?: string;
}

export const GiftPurchaseFlow: React.FC = () => {
  const { creatorId } = useParams<{ creatorId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Select Tier, 2: Configure Gift, 3: Payment
  const [formData, setFormData] = useState<GiftPurchaseData>({
    tierId: '',
    months: 1,
    recipientEmail: '',
    recipientName: '',
    message: '',
    deliverAt: ''
  });

  useEffect(() => {
    fetchTiers();
  }, [creatorId]);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`/api/creators/${creatorId}/tiers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setTiers(data.tiers || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
      setLoading(false);
    }
  };

  const handleTierSelect = (tierId: string) => {
    setFormData({ ...formData, tierId });
    setStep(2);
  };

  const handleConfigureGift = () => {
    if (!formData.months || formData.months < 1 || formData.months > 36) {
      alert('Please select duration between 1 and 36 months');
      return;
    }
    setStep(3);
  };

  const handlePurchase = async () => {
    try {
      const response = await fetch('/api/gifts/purchase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (response.ok && data.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkoutUrl;
      } else {
        alert(data.error || 'Failed to create gift purchase');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to process purchase');
    }
  };

  const selectedTier = tiers.find(t => t.id === formData.tierId);
  const totalAmount = selectedTier ? selectedTier.amount * formData.months : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div className={`flex items-center ${stepNum <= step ? 'text-orange-500' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  stepNum <= step ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
                }`}>
                  {stepNum}
                </div>
                <span className="ml-2 font-medium hidden md:inline">
                  {stepNum === 1 ? 'Select Tier' : stepNum === 2 ? 'Configure Gift' : 'Payment'}
                </span>
              </div>
              {stepNum < 3 && (
                <div className={`flex-1 h-1 mx-4 ${stepNum < step ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: Select Tier */}
      {step === 1 && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Select a Membership Tier to Gift</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                onClick={() => handleTierSelect(tier.id)}
                className="border rounded-lg p-6 hover:border-orange-500 hover:shadow-lg cursor-pointer transition-all"
              >
                <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                <div className="text-2xl font-bold text-orange-500 mb-4">
                  <LocalizedCurrency amount={tier.amount} currency={tier.currency} />
                  <span className="text-sm text-gray-500">/month</span>
                </div>
                <p className="text-gray-600 mb-4">{tier.description}</p>
                {tier.benefits && tier.benefits.length > 0 && (
                  <ul className="space-y-2">
                    {tier.benefits.slice(0, 3).map((benefit, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Configure Gift */}
      {step === 2 && selectedTier && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Configure Your Gift</h2>
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* Selected Tier Summary */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-gray-700 mb-2">Selected Tier:</h3>
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">{selectedTier.name}</span>
                <span className="text-xl font-bold text-orange-500">
                  <LocalizedCurrency amount={selectedTier.amount} currency={selectedTier.currency} />
                  <span className="text-sm text-gray-500">/month</span>
                </span>
              </div>
            </div>

            {/* Duration Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gift Duration (months)
              </label>
              <input
                type="number"
                min="1"
                max="36"
                value={formData.months}
                onChange={(e) => setFormData({ ...formData, months: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">Choose between 1 and 36 months</p>
            </div>

            {/* Recipient Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email (Optional)
              </label>
              <input
                type="email"
                value={formData.recipientEmail}
                onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                placeholder="recipient@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">Leave blank to get a gift code you can share yourself</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Name (Optional)
              </label>
              <input
                type="text"
                value={formData.recipientName}
                onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
                placeholder="John Doe"
                maxLength={120}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Personal Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Personal Message (Optional)
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Add a personal message to your gift..."
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>This message will be included with the gift</span>
                <span>{formData.message?.length || 0}/500</span>
              </div>
            </div>

            {/* Delivery Date (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send On (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.deliverAt}
                onChange={(e) => setFormData({ ...formData, deliverAt: e.target.value })}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">Schedule when the gift should be sent (leave blank to send immediately)</p>
            </div>

            {/* Total Amount */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total:</span>
                <span className="text-orange-500">
                  <LocalizedCurrency amount={totalAmount} currency={selectedTier.currency} />
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {formData.months} month{formData.months !== 1 ? 's' : ''} × <LocalizedCurrency amount={selectedTier.amount} currency={selectedTier.currency} />
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                {t('actions.back')}
              </button>
              <button
                onClick={handleConfigureGift}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                {t('actions.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Payment Summary */}
      {step === 3 && selectedTier && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Review and Purchase</h2>
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* Gift Summary */}
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Tier:</span>
                <span className="font-semibold">{selectedTier.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold">{formData.months} month{formData.months !== 1 ? 's' : ''}</span>
              </div>
              {formData.recipientEmail && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Recipient:</span>
                  <span className="font-semibold">{formData.recipientEmail}</span>
                </div>
              )}
              {formData.recipientName && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold">{formData.recipientName}</span>
                </div>
              )}
              {formData.message && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-2">Your Message:</p>
                  <p className="text-gray-800 italic">"{formData.message}"</p>
                </div>
              )}
            </div>

            {/* Total */}
            <div className="border-t border-b py-4">
              <div className="flex justify-between items-center text-2xl font-bold">
                <span>Total Amount:</span>
                <span className="text-orange-500">
                  <LocalizedCurrency amount={totalAmount} currency={selectedTier.currency} />
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setStep(2)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                {t('actions.back')}
              </button>
              <button
                onClick={handlePurchase}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                Proceed to Payment
              </button>
            </div>

            <p className="text-sm text-gray-500 text-center">
              You will be redirected to Stripe to complete your payment securely
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
